import React, { useCallback, useMemo } from 'react';
import 'react-international-phone/style.css';

import { Box, Button, Group, Input, Select, Skeleton, Stack, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendar, IconMail, IconTags } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import { PhoneInput } from 'react-international-phone';
import { phoneInputCountries } from 'utils/formatters';
import { notificationHandler } from 'utils/notifications';

import AthleteAddressInformation from './AthleteAddressInformation';
import AthleteMedicalInformation from './AthleteMedicalInformation';
import {
  AthleteInformationField,
  computeOnFileFields,
  isReturningBuyerOnFileMode,
  visibleAthleteInformationFields,
} from './onFileFields';
import OnFileSummary from './OnFileSummary';
import usePrepopulateAthleteInfoEffect from './usePrepopulateAthleteInfoEffect';
import useTargetAthleteProfile from './useTargetAthleteProfile';
import { useCheckout } from '../../CheckoutContext';

const AthleteInformation = (): JSX.Element => {
  const { completeStep, form, packageData } = useCheckout();
  const { errorNotification } = notificationHandler();
  const {
    gym: { country },
    packagesalesportalconfiguration: pkgConfig,
  } = packageData;

  const whoIsThisFor = form.values.whoIsThisFor;
  const emailIsEditable = whoIsThisFor === 'newLinkedAccount';

  const { dateFormat } = useLocalization(country);

  const { athleteProfile, isLoading: profileLoading } = useTargetAthleteProfile();
  usePrepopulateAthleteInfoEffect();

  // Returning buyer (spec Item 9): a buyer with a completed prior purchase buying for
  // themselves sees what is on file as plain text; only missing fields render as inputs.
  const onFileMode = isReturningBuyerOnFileMode(whoIsThisFor, athleteProfile);
  const lockedFields = useMemo<Set<AthleteInformationField>>(
    () =>
      onFileMode && athleteProfile
        ? computeOnFileFields(athleteProfile, pkgConfig)
        : new Set<AthleteInformationField>(),
    [onFileMode, athleteProfile, pkgConfig],
  );
  const isLocked = useCallback(
    (field: AthleteInformationField): boolean => lockedFields.has(field),
    [lockedFields],
  );
  const hasEditableFields = visibleAthleteInformationFields(pkgConfig).some(
    field => !lockedFields.has(field),
  );

  const handleContinue = useCallback((): void => {
    // Locked fields have no input to correct; validate only what the buyer can edit.
    if (!completeStep('athleteInformation', { lockedFields })) {
      errorNotification('Required fields are missing', 'Please fill in all required fields correctly');
    }
  }, [completeStep, lockedFields, errorNotification]);

  if (whoIsThisFor === 'myself' && profileLoading) {
    return (
      <Box px='md'>
        <Skeleton height={120} data-cy='athleteInformationLoading' />
      </Box>
    );
  }

  return (
    <Box px='md'>
      <Stack spacing='xs'>
        {onFileMode && athleteProfile && (
          <OnFileSummary
            athlete={athleteProfile}
            fields={lockedFields}
            dateFormat={dateFormat}
            hasEditableFields={hasEditableFields}
          />
        )}

        {!isLocked('firstName') && (
          <TextInput
            data-cy='athleteFirstNameInput'
            label='First Name'
            placeholder='Enter First Name'
            required
            icon={<IconTags />}
            {...form.getInputProps('athleteInformation.firstName')}
          />
        )}
        {!isLocked('lastName') && (
          <TextInput
            data-cy='athleteLastNameInput'
            label='Last Name'
            placeholder='Enter Last Name'
            required
            icon={<IconTags />}
            {...form.getInputProps('athleteInformation.lastName')}
          />
        )}
        <TextInput mt='-xs' type='hidden' {...form.getInputProps('athleteInformation.surname')} />
        {!onFileMode && (
          <TextInput
            data-cy='athleteEmailInput'
            type='email'
            label='Email'
            placeholder='Enter Email'
            required
            icon={<IconMail />}
            disabled={!emailIsEditable}
            {...form.getInputProps('athleteInformation.email')}
          />
        )}
        {!isLocked('phone') && (
          <Input.Wrapper
            label='Phone Number'
            required
            withAsterisk
            error={form.getInputProps('athleteInformation.phone').error}
          >
            <PhoneInput
              defaultCountry={country.toLowerCase()}
              countries={phoneInputCountries}
              placeholder='Enter Phone Number'
              style={{ position: 'relative', zIndex: 9999 }}
              inputProps={{ autoComplete: 'tel', name: 'tel' }}
              {...form.getInputProps('athleteInformation.phone')}
            />
          </Input.Wrapper>
        )}

        {pkgConfig.dobVisible && !isLocked('dob') && (
          <DateInput
            data-cy='dobInput'
            label='Date of Birth'
            placeholder={dateFormat}
            valueFormat={dateFormat}
            clearable
            defaultLevel='decade'
            {...form.getInputProps('athleteInformation.dob')}
            icon={<IconCalendar />}
            required={pkgConfig.dobRequired}
          />
        )}

        {pkgConfig.genderVisible && !isLocked('gender') && (
          <Select
            data-cy='genderSelectBox'
            label='Gender'
            placeholder='Select Gender'
            icon={<IconTags />}
            data={['Male', 'Female', 'Transgender', 'Non-binary/Non-conforming', 'Prefer not to respond']}
            {...form.getInputProps('athleteInformation.gender')}
            required={pkgConfig.genderRequired}
          />
        )}

        <AthleteAddressInformation locked={isLocked('address')} />

        <AthleteMedicalInformation lockedFields={lockedFields} />

        <Group position='right'>
          <Button data-cy='athleteInformationContinue' onClick={handleContinue}>
            Continue
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export default AthleteInformation;
